import { useMemo, useState, type ReactNode } from "react";
import { useQueries } from "@tanstack/react-query";
import { CategoryTabs } from "../components/CategoryTabs.tsx";
import { apiGet } from "../lib/api.ts";
import { fmtNum } from "../lib/csv.ts";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { ConfidenceBadge, GRADE_STYLE } from "../components/ConfidenceBadge.tsx";
import { ModelComparison } from "../components/ModelComparison.tsx";
import { theme } from "../theme.ts";
import type { AccuracyResponse, ConfidenceGrade, PaceStatus } from "@march/shared";
import { CATEGORY_LABEL } from "../lib/categories.ts";

type AType = "Frame" | "Cover" | "Base" | "Fabric" | "Trim" | "ProtectiveCover";
const TYPES: AType[] = ["Frame", "Cover", "Base", "Fabric", "Trim", "ProtectiveCover"];
const UNIT: Record<AType, string> = { Frame: "units", Cover: "units", Base: "units", Fabric: "yards", Trim: "yards", ProtectiveCover: "units" };

type CurRow = NonNullable<AccuracyResponse["current"]>["rows"][number];
type TrackRow = AccuracyResponse["track_record"][number] & { itemid: string };
type ConfRow = AccuracyResponse["confidence"][number];

const PACE: Record<PaceStatus, { label: string; fg: string; bg: string }> = {
  on_track: { label: "On track", fg: theme.color.successText, bg: theme.color.successFaint },
  running_high: { label: "Running high", fg: theme.color.warningText, bg: theme.color.warningFaint },
  running_low: { label: "Running low", fg: theme.color.brandInk, bg: theme.color.brandFaint },
  too_early: { label: "Too few to tell", fg: theme.color.textMuted, bg: theme.color.surfaceHover },
};

const pct = (x: number) => `${x > 0 ? "+" : ""}${Math.round(x * 100)}%`;
const fmtDate = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

function Card({ title, sub, children }: { title: string; sub?: ReactNode; children: ReactNode }) {
  return (
    <section
      style={{
        background: theme.color.surface,
        border: `1px solid ${theme.color.border}`,
        borderRadius: theme.radius.lg,
        padding: theme.space.lg,
        display: "grid",
        minWidth: 0, // wide tables scroll inside the card instead of stretching the page on phones
        gap: theme.space.md,
      }}
    >
      <div>
        <h2 style={{ margin: 0, fontSize: theme.font.size.lg }}>{title}</h2>
        {sub && (
          <div style={{ fontSize: theme.font.size.sm, color: theme.color.textMuted, marginTop: 4 }}>
            {sub}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={{ fontSize: theme.font.size.xs, color: theme.color.textMuted }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {note && <div style={{ fontSize: theme.font.size.xs, color: theme.color.textFaint }}>{note}</div>}
    </div>
  );
}

function Pill({ fg, bg, children }: { fg: string; bg: string; children: ReactNode }) {
  return (
    <span
      style={{
        padding: "1px 7px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: fg,
        background: bg,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/** "Off by" coloured by size, not sign: both directions cost money. */
function OffBy({ f, a }: { f: number; a: number }) {
  if (a <= 0) return <span style={{ color: theme.color.textFaint }}>—</span>;
  const e = f / a - 1;
  const color = Math.abs(e) <= 0.1 ? theme.color.successText : Math.abs(e) <= 0.25 ? theme.color.warningText : theme.color.dangerText;
  return <span style={{ color, fontWeight: 600 }}>{pct(e)}</span>;
}

/** "Show all N" under a capped table. Hidden when nothing is cut off. */
function MoreToggle({
  n,
  all,
  onToggle,
  label,
  shown = 15,
}: {
  n: number;
  all: boolean;
  onToggle: () => void;
  label: string;
  shown?: number;
}) {
  if (n <= shown) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        all: "unset",
        cursor: "pointer",
        justifySelf: "start",
        fontSize: theme.font.size.sm,
        color: theme.color.brand,
      }}
    >
      {all ? `Show the first ${shown}` : `Show all ${n} ${label}`}
    </button>
  );
}

export function AccuracyPage() {
  const [type, setType] = useState<AType>("Frame");
  const [paceFilter, setPaceFilter] = useState<PaceStatus | "all">("all");
  const [gradeFilter, setGradeFilter] = useState<ConfidenceGrade | "all">("all");
  const [showAllTrack, setShowAllTrack] = useState(false);
  const [showAllCur, setShowAllCur] = useState(false);
  const [showAllConf, setShowAllConf] = useState(false);

  // Every category (the tab counts need them); the selected one drives the page.
  const qs = useQueries({
    queries: TYPES.map((t) => ({
      queryKey: ["accuracy", t],
      queryFn: () => apiGet<AccuracyResponse>(`/api/accuracy?type=${t}`),
    })),
  });
  const q = qs[TYPES.indexOf(type)];
  const counts = Object.fromEntries(
    TYPES.map((t, i) => [t, (qs[i].data?.current?.rows ?? []).filter((r) => r.forecast > 0 || r.actual > 0).length]),
  ) as Record<AType, number>;
  const d = q.data;
  const unit = UNIT[type];
  const cur = d?.current ?? null;

  // ---- Current window ----
  const paceCounts = useMemo(() => {
    const c: Record<PaceStatus, number> = { on_track: 0, running_high: 0, running_low: 0, too_early: 0 };
    for (const r of cur?.rows ?? []) c[r.status]++;
    return c;
  }, [cur]);
  const curRows = useMemo(
    () =>
      (cur?.rows ?? [])
        .filter((r) => paceFilter === "all" || r.status === paceFilter)
        .filter((r) => r.forecast > 0 || r.actual > 0)
        .sort((a, b) => Math.abs(b.actual - b.expected) - Math.abs(a.actual - a.expected)),
    [cur, paceFilter],
  );
  const curCols: Column<CurRow>[] = [
    { key: "sku", header: "SKU", render: (r) => <b>{r.itemid}</b>, sortValue: (r) => r.itemid },
    {
      key: "f",
      header: "90-day forecast",
      align: "right",
      render: (r) => fmtNum(Math.round(r.forecast)),
      sortValue: (r) => r.forecast,
    },
    {
      key: "e",
      header: "Expected by now",
      align: "right",
      render: (r) => fmtNum(Math.round(r.expected)),
      sortValue: (r) => r.expected,
    },
    {
      key: "a",
      header: "Actual so far",
      align: "right",
      render: (r) => fmtNum(Math.round(r.actual)),
      sortValue: (r) => r.actual,
    },
    {
      key: "p",
      header: "Pace",
      align: "right",
      render: (r) => (r.expected >= 1 ? pct(r.actual / r.expected - 1) : "—"),
      sortValue: (r) => (r.expected >= 1 ? r.actual / r.expected - 1 : null),
    },
    {
      key: "s",
      header: "Status",
      render: (r) => (
        <Pill fg={PACE[r.status].fg} bg={PACE[r.status].bg}>
          {PACE[r.status].label}
        </Pill>
      ),
      sortValue: (r) => r.status,
    },
  ];

  // ---- Track record ----
  const track: TrackRow[] = useMemo(
    () => (d?.track_record ?? []).map((r) => ({ ...r, itemid: r.snapshot_date })),
    [d],
  );
  const recent = track.slice(0, 13); // about the last quarter of weekly windows
  const headline = useMemo(() => {
    if (!recent.length) return null;
    const f = recent.reduce((s, r) => s + r.forecast, 0);
    const a = recent.reduce((s, r) => s + r.actual, 0);
    const el = recent.reduce((s, r) => s + r.eligible, 0);
    const w25 = recent.reduce((s, r) => s + r.within25, 0);
    const w50 = recent.reduce((s, r) => s + r.within50, 0);
    return {
      bias: a > 0 ? f / a - 1 : 0,
      w25: el ? w25 / el : 0,
      w50: el ? w50 / el : 0,
      n: recent.length,
    };
  }, [recent]);
  const trackCols: Column<TrackRow>[] = [
    {
      key: "date",
      header: "Forecast made",
      render: (r) => fmtDate(r.snapshot_date),
      sortValue: (r) => r.snapshot_date,
    },
    {
      key: "win",
      header: "Window",
      render: (r) => `${fmtDate(r.window_from)} – ${fmtDate(r.window_to)}`,
    },
    {
      key: "kind",
      header: "Source",
      render: (r) =>
        r.kind === "live" ? (
          <Pill fg={theme.color.successText} bg={theme.color.successFaint}>Live</Pill>
        ) : (
          <span
            title="The same formula re-run as of this date, using only data from before it."
            style={{ color: theme.color.textMuted }}
          >
            Backtest
          </span>
        ),
      sortValue: (r) => r.kind,
    },
    {
      key: "f",
      header: "Forecast",
      align: "right",
      render: (r) => fmtNum(Math.round(r.forecast)),
      sortValue: (r) => r.forecast,
    },
    {
      key: "a",
      header: "Actual",
      align: "right",
      render: (r) => fmtNum(Math.round(r.actual)),
      sortValue: (r) => r.actual,
    },
    {
      key: "off",
      header: "Off by",
      align: "right",
      render: (r) => <OffBy f={r.forecast} a={r.actual} />,
      sortValue: (r) => (r.actual > 0 ? Math.abs(r.forecast / r.actual - 1) : null),
    },
    {
      key: "w25",
      header: "SKUs within ±25%",
      align: "right",
      render: (r) => (r.eligible ? `${Math.round((r.within25 / r.eligible) * 100)}% of ${r.eligible}` : "—"),
      sortValue: (r) => (r.eligible ? r.within25 / r.eligible : null),
    },
    {
      key: "w50",
      header: "Within ±50%",
      align: "right",
      render: (r) => (r.eligible ? `${Math.round((r.within50 / r.eligible) * 100)}%` : "—"),
      sortValue: (r) => (r.eligible ? r.within50 / r.eligible : null),
    },
  ];

  // ---- Confidence ----
  const gradeCounts = useMemo(() => {
    const c: Record<ConfidenceGrade, number> = { high: 0, medium: 0, low: 0, low_volume: 0, new: 0 };
    for (const r of d?.confidence ?? []) c[r.grade]++;
    return c;
  }, [d]);
  const confRows = useMemo(
    () =>
      (d?.confidence ?? [])
        .filter((r) => gradeFilter === "all" || r.grade === gradeFilter)
        .sort((a, b) => b.avg_actual - a.avg_actual),
    [d, gradeFilter],
  );
  const confCols: Column<ConfRow>[] = [
    { key: "sku", header: "SKU", render: (r) => <b>{r.itemid}</b>, sortValue: (r) => r.itemid },
    {
      key: "g",
      header: "Confidence",
      render: (r) => <ConfidenceBadge grade={r.grade} hitRate={r.hit_rate_25} />,
      sortValue: (r) => ["high", "medium", "low", "low_volume", "new"].indexOf(r.grade),
    },
    {
      key: "n",
      header: "Windows",
      align: "right",
      render: (r) => r.windows,
      sortValue: (r) => r.windows,
    },
    {
      key: "h25",
      header: "Within ±25%",
      align: "right",
      render: (r) => `${Math.round(r.hit_rate_25 * 100)}%`,
      sortValue: (r) => r.hit_rate_25,
    },
    {
      key: "h50",
      header: "Within ±50%",
      align: "right",
      render: (r) => `${Math.round(r.hit_rate_50 * 100)}%`,
      sortValue: (r) => r.hit_rate_50,
    },
    {
      key: "avg",
      header: `Avg 90-day ${unit}`,
      align: "right",
      render: (r) => fmtNum(Math.round(r.avg_actual)),
      sortValue: (r) => r.avg_actual,
    },
    {
      key: "bias",
      header: "Tends to run",
      align: "right",
      render: (r) =>
        r.bias == null ? "—" : Math.abs(r.bias) < 0.1 ? "about right" : `${pct(r.bias)} ${r.bias > 0 ? "high" : "low"}`,
      sortValue: (r) => r.bias,
    },
  ];

  const rules = d?.rules;

  return (
    <div className="u-page">
      <CategoryTabs items={TYPES} value={type} onChange={setType} label={(t) => CATEGORY_LABEL[t]} counts={counts} loading={qs.some((x) => x.isLoading)} />
      <p className="page-note">How the forecast has compared with what actually sold.</p>
      <div style={{ display: "grid", gap: theme.space.lg }}>

      {q.isLoading && <div style={{ color: theme.color.textMuted }}>Loading…</div>}
      {q.error && <div style={{ color: theme.color.danger }}>{(q.error as Error).message}</div>}

      {d && (
        <>
          <Card
            title="This window so far"
            sub={
              cur ? (
                <>
                  {fmtDate(cur.window_from)} – {fmtDate(cur.window_to)}
                  {cur.days_elapsed === 0 ? (
                    <> · starts today; tracking begins tomorrow.</>
                  ) : (
                    <span title={`Forecast frozen on ${fmtDate(cur.snapshot_date)}. "Expected by now" follows the shape of past years: by this point the window has usually seen ${Math.round(cur.expected_share * 100)}% of its demand.`}>
                      {" "}· day {cur.days_elapsed} of 90
                    </span>
                  )}
                </>
              ) : (
                "The first weekly snapshot is taken tonight. From tomorrow this shows how the current forecast is tracking."
              )
            }
          >
            {cur && cur.days_elapsed > 0 && (
              <>
                <div style={{ display: "flex", gap: theme.space.xl, flexWrap: "wrap" }}>
                  <Stat label={`90-day forecast (${unit})`} value={fmtNum(Math.round(cur.totals.forecast))} />
                  <Stat label="Expected by now" value={fmtNum(Math.round(cur.totals.expected))} />
                  <Stat label="Actual so far" value={fmtNum(Math.round(cur.totals.actual))} />
                  <Stat
                    label="Pace"
                    value={cur.totals.expected >= 1 ? pct(cur.totals.actual / cur.totals.expected - 1) : "—"}
                    note={cur.days_elapsed < 14 ? "Early days — expect this to move" : undefined}
                  />
                </div>
                <div className="strip" role="group" aria-label="Show" style={{ marginBottom: 0 }}>
                  <button type="button" aria-pressed={paceFilter === "all"} onClick={() => setPaceFilter("all")}>
                    <b>{Object.values(paceCounts).reduce((a, b) => a + b, 0)}</b>All
                  </button>
                  {(["running_high", "running_low", "on_track", "too_early"] as PaceStatus[]).map((s) => (
                    <button key={s} type="button" aria-pressed={paceFilter === s} onClick={() => setPaceFilter(paceFilter === s ? "all" : s)}>
                      <b>{paceCounts[s]}</b>{PACE[s].label}
                    </button>
                  ))}
                </div>
                <DataTable
                  storageKey="accuracy-current"
                  columns={curCols}
                  rows={showAllCur ? curRows : curRows.slice(0, 15)}
                  emptyMessage="No SKUs in this group."
                />
                <MoreToggle n={curRows.length} all={showAllCur} onToggle={() => setShowAllCur((v) => !v)} label="SKUs" />
              </>
            )}
          </Card>

          <Card
            title="Track record"
            sub={
              headline ? (
                <>
                  Over the last {headline.n} closed windows, the total forecast ran{" "}
                  <b>{pct(headline.bias)}</b> against what sold.{" "}
                  <b>{Math.round(headline.w25 * 100)}%</b> of SKUs selling {rules?.eligible_units}+{" "}
                  {unit} landed within ±25%, and <b>{Math.round(headline.w50 * 100)}%</b> within ±50%.
                </>
              ) : (
                "No forecast window has closed yet."
              )
            }
          >
            {track.length > 0 && (
              <>
                <DataTable
                  storageKey="accuracy-track"
                  columns={trackCols}
                  rows={showAllTrack ? track : track.slice(0, 13)}
                />
                <MoreToggle n={track.length} all={showAllTrack} onToggle={() => setShowAllTrack((v) => !v)} label="windows" shown={13} />
              </>
            )}
          </Card>

          <Card
            title="Model comparison"
            sub="Three alternative forecasting models, scored against the current one on exactly the same windows. Error is weighted by volume, so the SKUs that drive spend count most."
          >
            <ModelComparison type={type} />
          </Card>

          <Card
            title="Confidence by SKU"
            sub="How often each SKU's past forecasts landed close to what sold. Shown on the Forecast page too."
          >
            {d.confidence.length === 0 ? (
              <div style={{ color: theme.color.textMuted }}>No graded windows yet.</div>
            ) : (
              <>
                <div className="strip" role="group" aria-label="Show" style={{ marginBottom: 0 }}>
                  <button type="button" aria-pressed={gradeFilter === "all"} onClick={() => setGradeFilter("all")}>
                    <b>{d.confidence.length}</b>All
                  </button>
                  {(["high", "medium", "low", "low_volume", "new"] as ConfidenceGrade[]).map((g) => (
                    <button key={g} type="button" aria-pressed={gradeFilter === g} onClick={() => setGradeFilter(gradeFilter === g ? "all" : g)}>
                      <b>{gradeCounts[g]}</b>{GRADE_STYLE[g].label}
                    </button>
                  ))}
                </div>
                <DataTable
                  storageKey="accuracy-confidence"
                  columns={confCols}
                  rows={showAllConf ? confRows : confRows.slice(0, 15)}
                />
                <MoreToggle n={confRows.length} all={showAllConf} onToggle={() => setShowAllConf((v) => !v)} label="SKUs" />
              </>
            )}
          </Card>

          <Card title="How this is measured">
            <ul style={{ margin: 0, paddingLeft: 18, color: theme.color.textMuted, lineHeight: 1.6, fontSize: theme.font.size.sm }}>
              <li>
                Once a week the app freezes the forecast exactly as the Forecast page showed it. When
                that forecast's 90 days have passed, it is scored against the units actually used.
                Last frozen: {d.last_live_snapshot ? fmtDate(d.last_live_snapshot) : "not yet"}.
              </li>
              <li>
                <b>Backtest</b> rows are the same formula re-run as of past Mondays, using only data
                from before each date. They give a track record now instead of in 90 days. Most
                average two prior years where today's forecast averages three (history starts July
                2023), and they see today's corrected invoice data, so treat them as a close guide
                rather than an exact replay.
              </li>
              <li>
                Windows overlap — each week's forecast covers the next 90 days — so neighbouring rows
                are not independent results.
              </li>
              {rules && (
                <li>
                  "Within ±25%" in the track record counts SKUs with at least {rules.eligible_units}{" "}
                  {unit} forecast or sold. A SKU's grade needs {rules.min_windows}+ closed windows:{" "}
                  <b>High</b> if {Math.round(rules.grade_hit_rate * 100)}% of them landed within ±25%
                  (or {rules.hit_floor_units} {unit}), <b>Medium</b> if within ±50%, otherwise{" "}
                  <b>Low</b>. SKUs averaging under {rules.low_volume_units} {unit} per 90 days are
                  marked <b>Low volume</b> instead of graded.
                </li>
              )}
              <li>
                "Expected by now" spreads the 90-day forecast the way demand arrived in the same
                window in past years, so a spring ramp does not read as running low. A SKU is on
                track within ±25% of that, or a little wider for small numbers where a couple of
                orders either way is normal.
              </li>
            </ul>
          </Card>
        </>
      )}
      </div>
    </div>
  );
}
